"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ArrowRight, Calendar } from "lucide-react";

export default function MicrositeEventsScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0F0B1F] text-white overflow-hidden">
      <div
        className="absolute top-1/3 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
                {t("Auto-updated", "Auto-actualizado")}
              </div>
              <h2 className="text-4xl md:text-[56px] font-black tracking-[-0.03em] leading-[1.02] mb-6">
                {t("Add an event. ", "Agregas un evento. ")}
                <br />
                <span className="text-white/30">{t("Site updates.", "El sitio se actualiza.")}</span>
              </h2>
              <p className="text-[16px] font-medium text-white/55 leading-relaxed mb-6">
                {t(
                  "Create an event in the admin — it appears on the public site in seconds. Sold-out tier? The site reflects it. Sunday-morning DM \"hey what's tonight?\" → send the link.",
                  "Creas un evento en admin — aparece en el sitio público en segundos. ¿Tier sold-out? El sitio lo refleja. DM del domingo \"hey ¿qué hay esta noche?\" → mandas el link."
                )}
              </p>

              <div className="inline-flex items-center gap-2.5 bg-white/[0.04] border border-white/10 px-4 py-3 rounded-full">
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(244,114,182,0.15)" }}>
                  <Calendar className="w-3.5 h-3.5" style={{ color: "#F472B6" }} />
                </div>
                <div className="text-[13px] font-bold text-white">
                  {t("Real-time event sync", "Sync de eventos en vivo")}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Events list mockup */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[24px] p-6"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="text-[10px] font-black tracking-widest uppercase text-white/45">
                  {t("This month at NEON CLUB", "Este mes en NEON CLUB")}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#F472B6" }} />
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#F472B6" }}>
                    Live
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { date: "FRI · DEC 13", title: "NEON RUSH", sub: "DJ Marka · House", tag: "234 sold", color: "#7C3AED" },
                  { date: "SAT · DEC 14", title: "BIRRIA + BASS", sub: "Live cumbia · Sold out", tag: "SOLD OUT", color: "#FB923C", soldout: true },
                  { date: "FRI · DEC 20", title: "ARCADE NIGHT", sub: "Old-school games + free play", tag: "On sale", color: "#C4B5FD" },
                  { date: "SAT · DEC 21", title: "NYE PRE-PARTY", sub: "DJ Cristal · Latin · 21+", tag: "Early bird", color: "#F472B6" },
                ].map((ev, i) => (
                  <motion.div
                    key={ev.title}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.45, delay: 0.2 + 0.1 * i }}
                    className="bg-white/[0.03] rounded-[14px] p-3 flex items-center gap-4"
                  >
                    {/* Date block */}
                    <div
                      className="rounded-[10px] px-3 py-2 text-center text-white shrink-0"
                      style={{ backgroundColor: ev.color, minWidth: 70 }}
                    >
                      <div className="text-[8px] font-black uppercase tracking-widest leading-tight">
                        {ev.date.split(" · ")[0]}
                      </div>
                      <div className="text-[14px] font-black mt-0.5 leading-none tabular-nums">
                        {ev.date.split(" · ")[1]}
                      </div>
                    </div>

                    {/* Event copy */}
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-black text-white truncate">{ev.title}</div>
                      <div className="text-[11px] text-white/55 truncate">{ev.sub}</div>
                    </div>

                    {/* Tag */}
                    <div
                      className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full shrink-0"
                      style={{
                        backgroundColor: ev.soldout ? "rgba(251,146,60,0.18)" : "rgba(124,58,237,0.18)",
                        color: ev.soldout ? "#FB923C" : "#C4B5FD",
                      }}
                    >
                      {ev.tag}
                    </div>

                    <ArrowRight className="w-3.5 h-3.5 text-white/35 shrink-0" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
